import {
  AlfredPricingModule,
  AlfredPricingModuleCategory,
  AlfredQuote,
} from "./alfred-pricing"

/** Free features and per-unit messaging are part of every quote. */
export const isAlwaysIncludedAlfredModule = (
  module: AlfredPricingModule,
): boolean => {
  if (module.category === "MESSAGING") {
    return true
  }
  return (
    module.category === "FEATURE" &&
    !module.monthlyPriceSek &&
    !module.oneTimePriceSek
  )
}

const sumSek = (
  modules: AlfredPricingModule[],
  price: (module: AlfredPricingModule) => number | null,
): number => modules.reduce((sum, module) => sum + (price(module) ?? 0), 0)

/**
 * The price of a set of modules is always their sum. Rules:
 * - Exactly one USERS tier: the largest one asked for, else the smallest.
 * - At most one ONBOARDING: the largest one asked for.
 * - Free features and messaging are always included.
 * - A capped module is dropped when the module superseding it is billed.
 */
export const calculateAlfredQuote = (
  modules: AlfredPricingModule[],
  selection: { moduleIds: string[] },
): AlfredQuote => {
  const requested = new Set(selection.moduleIds)
  const sorted = [...modules].sort((a, b) => a.sortOrder - b.sortOrder)

  const largestRequested = (
    category: AlfredPricingModuleCategory,
  ): AlfredPricingModule | undefined => {
    const matches = sorted.filter(
      (m) => m.category === category && requested.has(m.id),
    )
    return matches[matches.length - 1]
  }

  const users =
    largestRequested("USERS") ?? sorted.find((m) => m.category === "USERS")
  const onboarding = largestRequested("ONBOARDING")

  const candidates = sorted.filter(
    (m) =>
      m.id === users?.id ||
      m.id === onboarding?.id ||
      (m.category === "FEATURE" &&
        (requested.has(m.id) || isAlwaysIncludedAlfredModule(m))) ||
      m.category === "MESSAGING",
  )
  const candidateIds = new Set(candidates.map((m) => m.id))
  const billed = candidates.filter(
    (m) => !m.supersededById || !candidateIds.has(m.supersededById),
  )

  return {
    moduleIds: billed.map((m) => m.id),
    monthlySek: sumSek(billed, (m) => m.monthlyPriceSek),
    oneTimeSek: sumSek(billed, (m) => m.oneTimePriceSek),
  }
}
